import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtFecha } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const INTENSIDAD = [
  "bg-white text-zinc-400 border-zinc-100",
  "bg-violet-100 text-violet-900 border-violet-200",
  "bg-violet-300 text-violet-950 border-violet-400",
  "bg-purple-400 text-white border-purple-500",
  "bg-purple-600 text-white border-purple-700",
  "bg-fuchsia-700 text-white border-fuchsia-800",
];

const DIAS = ["L", "M", "X", "J", "V", "S", "D"];

function MesMini({ mes, onDiaClick, onMesClick, diaSel, mesSel }) {
  const celdas = [];
  for (let i = 0; i < mes.primer_dia_semana; i++) {
    celdas.push(<div key={`e-${i}`} className="h-6" />);
  }
  mes.dias.forEach((d) => {
    const total = d.total;
    const intensidad = d.intensidad ?? (total > 0 ? Math.min(5, total) : 0);
    const sel = diaSel === d.fecha;
    const titulo = total > 0
      ? d.proyectos.map((p) => `#${p.codigo} ${p.nombre}`).join(", ")
      : "";
    celdas.push(
      <button
        key={d.fecha}
        type="button"
        title={titulo}
        onClick={() => onDiaClick(d, mes)}
        className={`flex h-6 w-full items-center justify-center rounded border text-[10px] font-medium transition hover:ring-2 hover:ring-violet-300 ${INTENSIDAD[intensidad]} ${sel ? "ring-2 ring-violet-600" : ""}`}
      >
        {d.dia}
      </button>,
    );
  });

  const badge = mes.proyectos_mes > 0
    ? `${mes.proyectos_mes} proy. · pico ${mes.pico_dia || 0}/día`
    : "0 proy.";

  return (
    <div className={`rounded-xl border bg-white p-3 shadow-sm ${mesSel === mes.mes ? "border-violet-400 ring-2 ring-violet-200" : "border-zinc-200"}`}>
      <div className="mb-2 flex items-center justify-between gap-1">
        <button
          type="button"
          onClick={() => onMesClick(mes)}
          className="text-left text-sm font-semibold text-zinc-800 hover:text-violet-700 hover:underline"
        >
          {mes.nombre}
        </button>
        <Badge variant="outline" className="shrink-0 text-[9px]">{badge}</Badge>
      </div>
      <div className="mb-1 grid grid-cols-7 gap-0.5 text-center text-[9px] font-semibold text-zinc-400">
        {DIAS.map((x) => <span key={x}>{x}</span>)}
      </div>
      <div className="grid grid-cols-7 gap-0.5">{celdas}</div>
    </div>
  );
}

function ListaProyectos({ items }) {
  if (!items?.length) {
    return <p className="mt-2 text-sm text-zinc-500">Ningún proyecto en este periodo.</p>;
  }
  return (
    <ul className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((p) => (
        <li key={p.proyecto_id} className="rounded-lg bg-white px-3 py-2 text-sm shadow-sm ring-1 ring-zinc-100">
          <div className="flex items-center gap-2">
            <span className="inline-block h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: p.color }} />
            <p className="font-medium text-zinc-900">#{p.codigo} {p.nombre}</p>
          </div>
          <p className="mt-1 text-xs font-medium text-violet-800">
            {fmtFecha(p.fecha_inicio)} — {fmtFecha(p.fecha_fin || p.fecha_inicio)}
          </p>
        </li>
      ))}
    </ul>
  );
}

export default function ProyectosCalendario() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [diaSel, setDiaSel] = useState(null);
  const [mesSel, setMesSel] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data: d } = await api.get("/proyectos/calendario-anual", { params: { year } });
      setData(d);
      setDiaSel(null);
      setMesSel(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCargando(false);
    }
  }, [year]);

  useEffect(() => { cargar(); }, [cargar]);

  const proyectosDia = useMemo(() => {
    if (!diaSel) return [];
    const uniq = new Map();
    (diaSel.proyectos || []).forEach((p) => uniq.set(p.proyecto_id, p));
    return [...uniq.values()];
  }, [diaSel]);

  const proyectosMes = useMemo(() => mesSel?.periodos_mes || [], [mesSel]);

  if (cargando && !data) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-violet-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-violet-100 bg-gradient-to-r from-violet-50/80 to-purple-50/80 p-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setYear((y) => y - 1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="min-w-[4rem] text-center text-lg font-semibold text-violet-900">{year}</span>
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setYear((y) => y + 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        {data?.max_dia > 0 && (
          <span className="text-xs text-zinc-600">Máx. proyectos concurrentes en un día: {data.max_dia}</span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 text-xs text-zinc-500">
        <span>Carga de proyectos:</span>
        {["Sin proyectos", "Baja", "Media-baja", "Media", "Alta", "Muy alta"].map((t, i) => (
          <span key={t} className="flex items-center gap-1">
            <span className={`inline-block h-4 w-4 rounded border ${INTENSIDAD[i]}`} />
            {t}
          </span>
        ))}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {(data?.meses || []).map((mes) => (
          <MesMini
            key={mes.mes}
            mes={mes}
            onDiaClick={(d) => { setDiaSel(d); setMesSel(null); }}
            onMesClick={(m) => { setMesSel(m); setDiaSel(null); }}
            diaSel={diaSel?.fecha}
            mesSel={mesSel?.mes}
          />
        ))}
      </div>

      {diaSel && (
        <div className="rounded-xl border border-violet-200 bg-violet-50/50 p-4">
          <p className="font-semibold text-violet-900">
            {fmtFecha(diaSel.fecha)} — {proyectosDia.length} proyecto{proyectosDia.length === 1 ? "" : "s"} activo{proyectosDia.length === 1 ? "" : "s"}
          </p>
          <ListaProyectos items={proyectosDia} />
        </div>
      )}

      {mesSel && !diaSel && (
        <div className="rounded-xl border border-purple-200 bg-purple-50/50 p-4">
          <p className="font-semibold text-purple-900">
            {mesSel.nombre} {year} — {proyectosMes.length} proyecto{proyectosMes.length === 1 ? "" : "s"}
          </p>
          <ListaProyectos items={proyectosMes} />
        </div>
      )}
    </div>
  );
}
